import { createContext, useContext, useEffect, useMemo, useState, type PropsWithChildren } from 'react';
import type { ThemeId } from '../config/themes';

interface ThemeContextValue {
  theme: ThemeId;
  setTheme: (theme: ThemeId) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readTheme(): ThemeId {
  try {
    const saved = typeof window !== 'undefined' ? window.localStorage.getItem('pilum-theme') : null;
    return saved === 'navy' || saved === 'ocean' || saved === 'mint' ? saved : 'mint';
  } catch {
    return 'mint';
  }
}

export function ThemeProvider({ children }: PropsWithChildren): React.JSX.Element {
  const [theme, setTheme] = useState<ThemeId>(readTheme);
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      window.localStorage.setItem('pilum-theme', theme);
    } catch {
      // Theme still works when storage is blocked by privacy settings.
    }
    const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (meta) meta.content = getComputedStyle(document.documentElement).getPropertyValue('--color-canvas').trim();
  }, [theme]);
  const value = useMemo(() => ({ theme, setTheme }), [theme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const value = useContext<ThemeContextValue | null>(ThemeContext);
  if (!value) throw new Error('useTheme must be used within ThemeProvider');
  return value;
}
